/**
 * Modal - Reusable centered modal overlay component.
 *
 * Renders a backdrop overlay with a centered white card panel. Supports
 * three size presets (sm, md, lg) and provides consistent brand styling
 * across all admin CRUD modals. Closes on Escape key press or when
 * clicking the backdrop outside the modal panel.
 *
 * Features:
 * - Keyboard accessible: closes on Escape key
 * - Click-outside-to-close via backdrop click detection
 * - Focus trap via aria-modal and role="dialog"
 * - Responsive sizing with max-width constraints
 * - Brand-colored header with close X button
 * - Scroll support for tall content via max-height + overflow
 *
 * @example
 * <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="Add User" size="md">
 *	   <form>...form fields...</form>
 * </Modal>
 */
import React, { useEffect, useCallback, useRef } from 'react';
import { useBodyScrollLock } from '../../../hooks/useBodyScrollLock';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/** Available modal width presets */
export type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

/**
 * Props for the Modal component.
 *
 * @property isOpen	  - Controls whether the modal is visible
 * @property onClose  - Callback fired when the user requests closing (Escape, backdrop click, or X button)
 * @property title	  - Text displayed in the modal header
 * @property children - The modal body content (forms, text, etc.)
 * @property size	  - Width preset: 'sm' (384px), 'md' (512px), 'lg' (640px). Defaults to 'md'.
 */
export interface ModalProps {
	isOpen: boolean;
	onClose: () => void;
	title: string;
	children: React.ReactNode;
	size?: ModalSize;
	/**
	 * Action row pinned to the bottom of the panel, OUTSIDE the scrollable body.
	 * Put Cancel/Save here rather than at the end of `children`: on a short
	 * laptop screen or a phone, buttons living inside the scroll area get
	 * pushed past the panel edge and read as clipped/unreachable.
	 */
	footer?: React.ReactNode;
	/** Optional outer className merged onto the root backdrop element. */
	className?: string;
	/**
	 * Overrides the data-ref marker so a composing organism (e.g. CsvImport,
	 * AddUserModal) keeps its own atomic identity. Defaults to the Modal marker.
	 */
	dataRef?: string;
}

/* ============================================
 * SIZE CLASS MAP
 * ============================================ */

/** Maps size presets to Tailwind max-width classes */
const SIZE_CLASSES: Record<ModalSize, string> = {
	sm: 'max-w-sm',	  /* 384px max width */
	md: 'max-w-lg',	  /* 512px max width */
	lg: 'max-w-[calc(100vw-32px)] tablet:max-w-lg desktop:max-w-2xl',  /* 640px max width */
	xl: 'max-w-[calc(100vw-32px)] tablet:max-w-2xl desktop:max-w-4xl',  /* 896px max width — for large forms like work order editing */
};

/* ============================================
 * COMPONENT
 * ============================================ */

const Modal: React.FC<ModalProps> = ({
	isOpen,
	onClose,
	title,
	children,
	size = 'md',
	footer,
	className,
	dataRef = 'organism-modal',
}) => {
	/* Ref for the modal panel — used to detect outside clicks */
	const panelRef = useRef<HTMLDivElement>(null);

	/**
	 * Handles keydown events on the document. Closes the modal when
	 * the user presses the Escape key, providing a standard keyboard
	 * interaction pattern for dismissing overlays.
	 */
	const handleKeyDown = useCallback(
		(e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				onClose();
			}
		},
		[onClose],
	);

	/* Attach and detach the Escape key listener when the modal opens/closes */
	useEffect(() => {
		if (!isOpen) return;
		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [isOpen, handleKeyDown]);

	/* Freeze the page behind the modal (shared, stack-safe lock) */
	useBodyScrollLock(isOpen);

	/**
	 * Handles clicks on the backdrop overlay. Only closes the modal
	 * if the click target is the backdrop itself (not the panel content).
	 * This allows users to interact with form fields inside the modal
	 * without accidentally closing it.
	 */
	const handleBackdropClick = useCallback(
		(e: React.MouseEvent<HTMLDivElement>) => {
			/* Only close if the click was directly on the backdrop, not on the panel */
			if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
				onClose();
			}
		},
		[onClose],
	);

	/* Don't render anything when the modal is closed */
	if (!isOpen) return null;

	return (
		/* Full-screen backdrop overlay with semi-transparent black background.
		   z-[60] layers above the BottomTabBar's z-50 so the modal covers the
		   mobile tab bar instead of being overlapped by it. Bottom padding
		   grows with the safe-area inset on home-indicator devices. */
		<div
			className={['modal', 'fixed inset-0 z-[60] max-h-[100dvh] flex items-center justify-center bg-black/50 p-16px pb-[max(16px,env(safe-area-inset-bottom,0px))]', className ?? ''].join(' ')}
			onClick={handleBackdropClick}
			data-ref={dataRef}
			role="dialog"
			aria-modal="true"
			aria-label={title}
		>
			{/* Modal panel card — centered, scrollable for tall content.
			    max-h-full sizes against the fixed backdrop (the real visible
			    viewport, minus its padding), which unlike 90vh stays correct on
			    mobile browsers with collapsible chrome. */}
			<div
				ref={panelRef}
				className={`modal__panel bg-white rounded-lg shadow-xl w-full ${SIZE_CLASSES[size]} max-h-full flex flex-col`}
			>
				{/* --- Modal header with title and close button --- */}
				<div className="modal__header flex items-center justify-between px-24px pt-24px pb-16px border-b border-brand-border">
					<h3 className="modal__title text-18px font-bold font-heading text-brand-heading">
						{title}
					</h3>
					{/* Close X button */}
					<button
						type="button"
						onClick={onClose}
						className="flex items-center justify-center w-44px h-44px -mr-8px text-brand-text-subtle hover:text-brand-text transition-colors rounded-md hover:bg-brand-surface"
						aria-label="Close modal"
					>
						<svg
							className="h-20px w-20px"
							fill="none"
							viewBox="0 0 24 24"
							strokeWidth="1.5"
							stroke="currentColor"
						>
							<path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
						</svg>
					</button>
				</div>

				{/* --- Modal body — scrollable content area ---
				    min-h-0 is required for the flex child to actually shrink and
				    scroll; without it the body grows to its content height and
				    pushes the footer past the bottom of the panel. */}
				<div className="modal__body px-24px py-16px overflow-y-auto flex-1 min-h-0">
					{children}
				</div>

				{/* --- Pinned footer (optional) ---
				    Sits outside the scroll area so Cancel/Save stay visible and
				    tappable no matter how tall the form is or how short the
				    screen. flex-shrink-0 keeps it from being squeezed away. */}
				{footer && (
					<div className="modal__footer flex-shrink-0 px-24px py-16px border-t border-brand-border bg-white rounded-b-lg">
						{footer}
					</div>
				)}
			</div>
		</div>
	);
};

export default Modal;
